import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Brain, 
  TrendingUp, 
  DollarSign, 
  Sparkles, 
  RefreshCw, 
  Lock,
  ArrowLeft,
  Building2,
  Zap,
  PieChart,
  Activity,
  Clock,
  Award,
  BarChart3,
  Eye,
  Users,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OPARA v7.0 - Intelligence Dashboard
 * Admin dashboard for visualizing OPARA's brand intelligence growth
 * 
 * SECURITY: Requires admin password (same as /adminboraorkun)
 * 
 * Metrics:
 * 1. Industry Dominance - Which sectors have most brand collaborations
 * 2. Learning Rate by Source - DB vs AI-verified brands
 * 3. LLM Savings Rate - Cost optimization metrics
 * 4. Discovery Feed - Last 24h AI-verified brands
 */
export default function IntelligenceDashboard() {
  const navigate = useNavigate();
  
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(null);

  // Dashboard states
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [detectedBrands, setDetectedBrands] = useState(null);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [error, setError] = useState(null);

  // Check for existing admin token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setAdminToken(token);
      setIsAuthenticated(true);
    } else {
      // 2FA-baypas kapatildi: bagimsiz login YOK. Tek giris kapisi AdminPage (email + 2FA).
      // Token yoksa admin paneline yonlendir; oradan 2FA ile girilir.
      navigate(`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}`);
    }
  }, [navigate]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    setIsAuthenticated(false);
    setDashboardData(null);
    toast.info("Çıkış yapıldı");
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async (showRefreshToast = false) => {
    if (!adminToken) return;
    
    try {
      setLoading(true);
      if (showRefreshToast) setRefreshing(true);
      
      // Fetch both dashboard data and detected brands in parallel
      const [dashboardRes, brandsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/intelligence/dashboard`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/intelligence/detected-brands`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);
      
      setDashboardData(dashboardRes.data);
      setDetectedBrands(brandsRes.data);
      setError(null);
      
      if (showRefreshToast) {
        toast.success("Dashboard güncellendi", {
          description: `${brandsRes.data.total_unique_brands} benzersiz marka, ${brandsRes.data.total_detections} toplam tespit`
        });
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Token expired or invalid
        localStorage.removeItem('admin_token');
        setAdminToken(null);
        setIsAuthenticated(false);
        setError("Oturum süresi doldu. Lütfen tekrar giriş yapın.");
      } else {
        setError("Dashboard verileri yüklenirken hata oluştu.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminToken]);

  // Clear brand history
  const handleClearHistory = async () => {
    if (!window.confirm("Marka sorgu geçmişini silmek istediğinize emin misiniz?")) return;
    
    try {
      setClearingHistory(true);
      await axios.delete(`${API_URL}/api/admin/intelligence/brand-history`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Marka sorgu geçmişi silindi");
      fetchDashboard(false);
    } catch (err) {
      toast.error("Silme işlemi başarısız", {
        description: err.response?.data?.detail || "Bir hata oluştu"
      });
    } finally {
      setClearingHistory(false);
    }
  };

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && adminToken) {
      fetchDashboard();
    }
  }, [isAuthenticated, adminToken, fetchDashboard]);

  // Bagimsiz login KALDIRILDI (2FA-baypas yuzeyi). Token yoksa useEffect AdminPage'e yonlendirir;
  // yonlendirme tamamlanana dek hicbir sey render etme.
  if (!isAuthenticated) {
    return null;
  }

  // Category color mapping
  const getCategoryColor = (category) => {
    const colors = {
      "Technology": "bg-blue-500/20 text-blue-400 border-blue-500/30",
      "Fashion & Apparel": "bg-pink-500/20 text-pink-400 border-pink-500/30",
      "Cosmetics & Beauty": "bg-purple-500/20 text-purple-400 border-purple-500/30",
      "Food & Beverage": "bg-orange-500/20 text-orange-400 border-orange-500/30",
      "Automotive": "bg-red-500/20 text-red-400 border-red-500/30",
      "E-commerce": "bg-green-500/20 text-green-400 border-green-500/30",
      "Local/UAE": "bg-amber-500/20 text-amber-400 border-amber-500/30",
      "Entertainment": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      "Sports": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      "Finance": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      "Unknown": "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    return colors[category] || colors["Unknown"];
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 60) return `${diffMins}dk önce`;
    if (diffHours < 24) return `${diffHours}sa önce`;
    return date.toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Brain className="h-12 w-12 text-[#FFDD59] mx-auto animate-pulse" />
          <p className="text-gray-400 mt-4">OPARA Zeka Paneli Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border-red-500/30 max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-red-400 mb-4">⚠️</div>
            <h3 className="text-white font-semibold mb-2">Erişim Hatası</h3>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <div className="flex gap-2">
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Tekrar Giriş
              </Button>
              <Button 
                onClick={() => navigate('/')}
                className="flex-1 bg-[#FFDD59] text-black hover:bg-[#FFDD59]/90"
              >
                Ana Sayfa
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const data = dashboardData;

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col" data-testid="intelligence-dashboard">
      {/* Admin Navigation - Same as AdminPage */}
      <div className="bg-[#0D0D0D]/95 backdrop-blur-sm py-4 border-b border-gray-800">
        <div className="flex items-center justify-between max-w-7xl mx-auto px-4">
          {/* Admin Panel Title - Sol */}
          <div className="flex items-center gap-3">
            <Lock className="w-8 h-8 text-red-500" />
            <h1 className="text-2xl font-bold text-red-500">Admin Panel</h1>
          </div>
          
          {/* Tabs - Orta */}
          <div className="bg-gray-800 rounded-lg p-1 flex gap-2">
            <button
              onClick={() => navigate(`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}?tab=whitelist`)}
              className="px-6 py-3 rounded-md font-bold transition-colors text-base text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <Users className="w-5 h-5 inline mr-2" />
              Ekip Yönetimi
            </button>
            <button
              onClick={() => navigate(`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}?tab=dashboard`)}
              className="px-6 py-3 rounded-md font-bold transition-colors text-base text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <Activity className="w-5 h-5 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => navigate(`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}?tab=userstats`)}
              className="px-6 py-3 rounded-md font-bold transition-colors text-base text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Kullanıcı Analitik
            </button>
            <button
              onClick={() => navigate(`/${process.env.REACT_APP_ADMIN_PATH || 'adminboraorkun'}?tab=reels`)}
              className="px-6 py-3 rounded-md font-bold transition-colors text-base text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <Eye className="w-5 h-5 inline mr-2" />
              Reels Snapshot
            </button>
            <button
              className="px-6 py-3 rounded-md font-bold transition-colors text-base bg-[#FFDD59] text-black"
            >
              <Brain className="w-5 h-5 inline mr-2" />
              Intelligence
            </button>
          </div>
          
          {/* Actions - Sağ */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Ana Sayfa
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <Lock className="h-4 w-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Brain className="h-8 w-8 text-[#FFDD59]" />
            OPARA Global Zeka Metrikleri
          </h1>
          <p className="text-gray-400 mt-2">
            Son güncelleme: {data?.generated_at ? new Date(data.generated_at).toLocaleString('tr-TR') : '-'}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Brands */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#252525] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Toplam Marka</p>
                  <p className="text-3xl font-bold text-white">{formatNumber(data?.summary?.total_brands || 0)}</p>
                </div>
                <Building2 className="h-10 w-10 text-[#FFDD59]/50" />
              </div>
              <div className="mt-4 flex gap-2">
                <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                  Seed: {data?.summary?.seed_brands || 0}
                </Badge>
                <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                  AI: {data?.summary?.ai_verified_brands || 0}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* AI Learning Rate */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#252525] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Zeka Büyüme Oranı</p>
                  <p className="text-3xl font-bold text-white">{data?.summary?.intelligence_growth_rate || 0}%</p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-500/50" />
              </div>
              <div className="mt-4">
                <Progress 
                  value={Math.min(data?.summary?.intelligence_growth_rate || 0, 100)} 
                  className="h-2 bg-gray-700"
                />
              </div>
            </CardContent>
          </Card>

          {/* LLM Savings */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#252525] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">LLM Tasarruf</p>
                  <p className="text-3xl font-bold text-green-400">${data?.llm_savings?.estimated_savings_usd || 0}</p>
                </div>
                <DollarSign className="h-10 w-10 text-green-500/50" />
              </div>
              <div className="mt-4 text-xs text-gray-500">
                {formatNumber(data?.llm_savings?.db_lookups_saved || 0)} DB lookup ile tasarruf
              </div>
            </CardContent>
          </Card>

          {/* 24h Discoveries */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#252525] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Son 24 Saat</p>
                  <p className="text-3xl font-bold text-purple-400">{data?.discovery_feed?.count_24h || 0}</p>
                </div>
                <Sparkles className="h-10 w-10 text-purple-500/50" />
              </div>
              <div className="mt-4 flex gap-2 text-xs">
                <span className="text-gray-500">7g: {data?.discovery_feed?.count_7d || 0}</span>
                <span className="text-gray-500">30g: {data?.discovery_feed?.count_30d || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Industry Dominance */}
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChart className="h-5 w-5 text-[#FFDD59]" />
                Sektörel Domine Etme Oranı
              </CardTitle>
              <CardDescription className="text-gray-500">
                Hangi sektör en çok iş birliği yapıyor?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data?.industry_dominance?.map((industry, idx) => (
                  <div key={industry.category} className="flex items-center gap-3">
                    <span className="text-gray-500 text-sm w-4">{idx + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-300 text-sm">{industry.category}</span>
                        <span className="text-[#FFDD59] text-sm font-medium">{industry.percentage}%</span>
                      </div>
                      <Progress value={industry.percentage} className="h-1.5 bg-gray-700" />
                    </div>
                    <Badge variant="outline" className="text-xs border-gray-700 text-gray-400">
                      {formatNumber(industry.detections)}
                    </Badge>
                  </div>
                ))}
                {(!data?.industry_dominance || data.industry_dominance.length === 0) && (
                  <p className="text-gray-500 text-sm text-center py-4">Henüz veri yok</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* LLM Efficiency */}
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-400" />
                LLM Verimlilik Skoru
              </CardTitle>
              <CardDescription className="text-gray-500">
                DB Lookup vs LLM Call tasarruf oranı
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#333"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#22c55e"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(data?.llm_savings?.efficiency_score || 0) * 3.52} 352`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <span className="absolute text-3xl font-bold text-white">
                    {data?.llm_savings?.efficiency_score || 0}%
                  </span>
                </div>
                <p className="text-gray-400 mt-4 text-sm">Verimlilik Skoru</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-800">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{formatNumber(data?.llm_savings?.db_lookups_saved || 0)}</p>
                  <p className="text-xs text-gray-500">DB Lookup (Ücretsiz)</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400">{data?.llm_savings?.ai_calls_made || 0}</p>
                  <p className="text-xs text-gray-500">LLM Call (Ücretli)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Discovery Feed */}
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-400" />
                Yeni Keşfedilenler (24 Saat)
              </CardTitle>
              <CardDescription className="text-gray-500">
                AI Hakem&apos;in otomatik eklediği markalar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data?.discovery_feed?.last_24h?.map((brand, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 rounded-lg bg-[#252525] hover:bg-[#2A2A2A] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-4 w-4 text-purple-400" />
                      <div>
                        <p className="text-white text-sm font-medium">{brand.name}</p>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] ${getCategoryColor(brand.category)}`}
                        >
                          {brand.category}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{formatTimeAgo(brand.discovered_at)}</p>
                    </div>
                  </div>
                ))}
                {(!data?.discovery_feed?.last_24h || data.discovery_feed.last_24h.length === 0) && (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Son 24 saatte yeni keşif yok</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Detected Brands - From Real Analyses */}
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Award className="h-5 w-5 text-[#FFDD59]" />
                    Analizlerde Tespit Edilen Markalar
                  </CardTitle>
                  <CardDescription className="text-gray-500">
                    {detectedBrands?.total_unique_brands || 0} benzersiz marka, {detectedBrands?.total_detections || 0} toplam tespit
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearHistory}
                  disabled={clearingHistory}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  {clearingHistory ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Geçmişi Sil
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {detectedBrands?.top_brands?.map((brand, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 rounded-lg bg-[#252525]"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-[#FFDD59] text-black' : 
                        idx === 1 ? 'bg-gray-300 text-black' : 
                        idx === 2 ? 'bg-amber-600 text-white' : 
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-white text-sm font-medium">@{brand.name}</p>
                        <p className="text-xs text-gray-500">
                          {brand.influencers?.length || 0} influencer'da tespit edildi
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-gray-500" />
                      <span className="text-[#FFDD59] font-medium">{brand.detection_count}x</span>
                    </div>
                  </div>
                ))}
                {(!detectedBrands?.top_brands || detectedBrands.top_brands.length === 0) && (
                  <div className="text-center py-8">
                    <Building2 className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Henüz marka tespit edilmedi</p>
                    <p className="text-gray-600 text-xs mt-1">Analiz yapıldıkça markalar burada görünecek</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Learning by Category */}
        <Card className="bg-[#1A1A1A] border-gray-800 mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-400" />
              AI Hakem Öğrenme Dağılımı
            </CardTitle>
            <CardDescription className="text-gray-500">
              Hangi kategorilerde AI en çok marka keşfediyor?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {data?.ai_learning_by_category?.map((cat) => (
                <Badge 
                  key={cat.category}
                  variant="outline"
                  className={`px-3 py-1.5 text-sm ${getCategoryColor(cat.category)}`}
                >
                  {cat.category}: {cat.count}
                </Badge>
              ))}
              {(!data?.ai_learning_by_category || data.ai_learning_by_category.length === 0) && (
                <p className="text-gray-500 text-sm">AI henüz yeni marka keşfetmedi</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
